// File: /var/www/app2/frontend/src/components/MissionaryManagement.tsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { User } from "../App";
import "../styles/MissionaryManagement.css";

// Interfaces for data structures
interface Missionary {
  id: number;
  name: string;
  number: string;
  country: string;
  state: string;
  location: string;
  is_active: boolean;
}

interface FormData {
  name: string;
  state: string;
  location: string;
}

interface SortConfig {
  key: string;
  direction: "asc" | "desc";
}

interface MissionaryManagementProps {
  user: User;
}

function MissionaryManagement({ user }: MissionaryManagementProps): React.ReactElement {
  const [missionaries, setMissionaries] = useState<Missionary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingMissionary, setEditingMissionary] = useState<Missionary | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    state: "",
    location: "",
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterCountry, setFilterCountry] = useState<string>("");
  const [countries, setCountries] = useState<string[]>([]);
  const [success, setSuccess] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "number",
    direction: "asc",
  });

  useEffect((): void => {
    fetchMissionaries();
    fetchCountries();
  }, []);

  const isAdmin = user.role === "admin" || user.role === "superadmin";

  const fetchMissionaries = async (): Promise<void> => {
    try {
      const url = isAdmin
        ? "/api/missionaries"
        : `/api/users/${user.id}/missionaries`;
      const response = await axios.get(url);
      setMissionaries(response.data.missionaries || []);
    } catch (error: any) {
      console.error("Failed to fetch missionaries:", error);
      setError("Failed to load missionaries");
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async (): Promise<void> => {
    try {
      const response = await axios.get("/api/missionaries/countries");
      setCountries(response.data.countries || []);
    } catch (error: any) {
      console.error("Failed to fetch countries:", error);
    }
  };

  const handleEdit = (missionary: Missionary): void => {
    setEditingMissionary(missionary);
    setFormData({
      name: missionary.name || "",
      state: missionary.state || "",
      location: missionary.location || "",
    });
    setError("");
    setSuccess("");
  };

  const handleCancel = (): void => {
    setEditingMissionary(null);
    setFormData({
      name: "",
      state: "",
      location: "",
    });
    setError("");
    setSuccess("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const response = await axios.put(
        `/api/missionaries/${editingMissionary?.id}`,
        formData
      );

      if (response.data.success) {
        setSuccess(`Missionary ${formData.name} updated successfully!`);
        fetchMissionaries();
        setTimeout((): void => {
          handleCancel();
        }, 2000);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to update missionary");
    }
  };

  const handleDelete = async (missionary: Missionary): Promise<void> => {
    if (!window.confirm(`Are you sure you want to delete missionary ${missionary.name} (${missionary.number})? This action can be undone by an administrator.`)) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      const response = await axios.delete(`/api/missionaries/${missionary.id}`);

      if (response.data.success) {
        setSuccess(`Missionary ${missionary.name} deleted successfully!`);
        fetchMissionaries();
      }
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to delete missionary");
    }
  };

  const handleToggleActive = async (missionary: Missionary): Promise<void> => {
    const newStatus = missionary.is_active === false ? "active" : "inactive";
    if (!window.confirm(`Are you sure you want to mark ${missionary.name} (${missionary.number}) as ${newStatus}?`)) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      const response = await axios.patch(`/api/missionaries/${missionary.id}/toggle-active`);

      if (response.data.success) {
        setSuccess(response.data.message);
        fetchMissionaries();
      }
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to update missionary status");
    }
  };

  // Filter missionaries based on search term and country
  const filteredMissionaries: Missionary[] = missionaries.filter(
    (emp: Missionary): boolean => {
      const matchesSearch = !searchTerm ||
        emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.location?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCountry = !filterCountry || emp.country === filterCountry;
      return matchesSearch && matchesCountry;
    }
  );

  // Sorting function
  const handleSort = (key: string): void => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Get sorted missionaries
  const getSortedMissionaries = (): Missionary[] => {
    if (!sortConfig.key) return filteredMissionaries;

    const sortedMissionaries = [...filteredMissionaries].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case "number":
          // Sort by missionary number (format: XX-YY-NNN)
          return sortConfig.direction === "asc"
            ? (a.number || "").localeCompare(b.number || "", undefined, { numeric: true })
            : (b.number || "").localeCompare(a.number || "", undefined, { numeric: true });
        case "name":
          aValue = a.name?.toLowerCase() || "";
          bValue = b.name?.toLowerCase() || "";
          break;
        case "country":
          // Sort by country, then by number within same country
          const countryCmp = (a.country?.toLowerCase() || "").localeCompare(b.country?.toLowerCase() || "");
          if (countryCmp !== 0) {
            return sortConfig.direction === "asc" ? countryCmp : -countryCmp;
          }
          return sortConfig.direction === "asc"
            ? (a.number || "").localeCompare(b.number || "", undefined, { numeric: true })
            : (b.number || "").localeCompare(a.number || "", undefined, { numeric: true });
        case "state":
          // Sort by state, then by number within same state
          const stateCmp = (a.state?.toLowerCase() || "").localeCompare(b.state?.toLowerCase() || "");
          if (stateCmp !== 0) {
            return sortConfig.direction === "asc" ? stateCmp : -stateCmp;
          }
          return sortConfig.direction === "asc"
            ? (a.number || "").localeCompare(b.number || "", undefined, { numeric: true })
            : (b.number || "").localeCompare(a.number || "", undefined, { numeric: true });
        case "location":
          // Sort by location, then by number within same location
          const locCmp = (a.location?.toLowerCase() || "").localeCompare(b.location?.toLowerCase() || "");
          if (locCmp !== 0) {
            return sortConfig.direction === "asc" ? locCmp : -locCmp;
          }
          return sortConfig.direction === "asc"
            ? (a.number || "").localeCompare(b.number || "", undefined, { numeric: true })
            : (b.number || "").localeCompare(a.number || "", undefined, { numeric: true });
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortedMissionaries;
  };

  const sortedMissionaries = getSortedMissionaries();

  if (loading) {
    return <div className="missionaries-loading">Loading missionaries...</div>;
  }

  return (
    <div className="missionaries-container">
      <h2>{isAdmin ? "Missionary Management" : "My Missionaries"}</h2>

      {/* Stats Section */}
      <div className="missionaries-stats">
        <div className="missionaries-stat-item">
          <span className="missionaries-stat-label">Total:</span>
          <span className="missionaries-stat-value total">{missionaries.length}</span>
        </div>
        <div className="missionaries-stat-item">
          <span className="missionaries-stat-label">Active:</span>
          <span className="missionaries-stat-value active">{missionaries.filter(m => m.is_active !== false).length}</span>
        </div>
        {isAdmin && (
          <div className="missionaries-stat-item">
            <span className="missionaries-stat-label">Inactive:</span>
            <span className="missionaries-stat-value inactive">{missionaries.filter(m => m.is_active === false).length}</span>
          </div>
        )}
        {isAdmin && (
          <div className="missionaries-stat-item">
            <span className="missionaries-stat-label">Showing:</span>
            <span className="missionaries-stat-value">{filteredMissionaries.length}</span>
          </div>
        )}
      </div>

      {/* Global Success/Error Messages */}
      {success && !editingMissionary && (
        <div className="missionaries-success-message" style={{ marginBottom: '20px' }}>{success}</div>
      )}
      {error && !editingMissionary && (
        <div className="missionaries-error-message" style={{ marginBottom: '20px' }}>{error}</div>
      )}

      {/* Search/Filter Section */}
      <div className="missionaries-filters-section">
        <div className="missionaries-search-group">
          <label>Search Missionaries</label>
          <input
            type="text"
            placeholder="Search by name, number, state, or location..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setSearchTerm(e.target.value)}
            className="missionaries-search-input"
          />
        </div>
        <div className="missionaries-search-group">
          <label>Country</label>
          <select
            value={filterCountry}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => setFilterCountry(e.target.value)}
            className="missionaries-search-input"
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Edit Modal (admin only) */}
      {isAdmin && editingMissionary && (
        <div className="missionaries-modal-overlay" onClick={handleCancel}>
          <div className="missionaries-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="missionaries-modal-header">
              <h3>Edit Missionary: {editingMissionary.number}</h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="missionaries-modal-body">
                <div className="missionaries-form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter missionary full name"
                  />
                </div>

                <div className="missionaries-form-row">
                  <div className="missionaries-form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      placeholder="e.g., California"
                    />
                  </div>

                  <div className="missionaries-form-group">
                    <label>District</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Fresno"
                    />
                  </div>
                </div>

                {error && <div className="missionaries-error-message">{error}</div>}
                {success && <div className="missionaries-success-message">{success}</div>}
              </div>

              <div className="missionaries-modal-footer">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="missionaries-cancel-btn"
                >
                  Cancel
                </button>
                <button type="submit" className="missionaries-submit-btn">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Missionary Table */}
      <div className="missionaries-table-container">
        <table className="missionaries-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("number")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Missionary Number{" "}
                <span
                  style={{
                    color: sortConfig.key === "number" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "number"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th
                onClick={() => handleSort("name")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Name{" "}
                <span
                  style={{
                    color: sortConfig.key === "name" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "name"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th
                onClick={() => handleSort("country")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Country{" "}
                <span
                  style={{
                    color: sortConfig.key === "country" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "country"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th
                onClick={() => handleSort("state")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                State{" "}
                <span
                  style={{
                    color: sortConfig.key === "state" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "state"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th
                onClick={() => handleSort("location")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Location{" "}
                <span
                  style={{
                    color: sortConfig.key === "location" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "location"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th>Status</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedMissionaries.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="no-data">
                  {searchTerm
                    ? "No missionaries found matching your search"
                    : "No missionaries registered"}
                </td>
              </tr>
            ) : (
              sortedMissionaries.map((missionary: Missionary) => (
                <tr key={missionary.id} className={missionary.is_active === false ? "inactive-row" : ""}>
                  <td>
                    <strong>{missionary.number}</strong>
                  </td>
                  <td>
                    {missionary.name}
                    {missionary.name === `Missionary ${missionary.number}` && (
                      <span className="needs-update-badge">(needs update)</span>
                    )}
                  </td>
                  <td>{missionary.country}</td>
                  <td>{missionary.state}</td>
                  <td>{missionary.location}</td>
                  <td>
                    <span className={`status-badge ${missionary.is_active === false ? "inactive" : "active"}`}>
                      {missionary.is_active === false ? "Inactive" : "Active"}
                    </span>
                  </td>
                  {isAdmin && (
                    <td>
                      <div className="missionaries-action-buttons">
                        <button
                          onClick={(): void => handleEdit(missionary)}
                          className="missionaries-edit-btn"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(): void => { handleToggleActive(missionary); }}
                          className={missionary.is_active === false ? "missionaries-activate-btn" : "missionaries-deactivate-btn"}
                        >
                          {missionary.is_active === false ? "Activate" : "Deactivate"}
                        </button>
                        <button
                          onClick={(): void => { handleDelete(missionary); }}
                          className="missionaries-delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MissionaryManagement;
